package com.macrofocus.eflux.network

import com.macrofocus.common.json.JSON
import com.macrofocus.common.json.JsonValue
import com.macrofocus.common.json.asJsonArray
import com.macrofocus.common.units.*
import com.macrofocus.eflux.network.line.LazyGeometryLineData
import com.macrofocus.eflux.network.line.PowerLineData
import com.macrofocus.eflux.network.line.RiskLineData
import com.macrofocus.eflux.network.line.WeatherLineData
import com.macrofocus.eflux.network.logical.logicalBranchIdOf
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.physical.PhysicalNetwork
import com.macrofocus.eflux.network.substation.EODSubstationData
import com.macrofocus.eflux.network.substation.LazyGeometrySubstationData
import com.macrofocus.eflux.network.substation.PowerSubstationData
import org.locationtech.jts.geom.Geometry
import org.molap.dataframe.DataFrame
import org.molap.dataframe.reindexRowsUsingColumnDefault
import org.molap.datetime.DateTimeTz
import org.molap.questdb.DateTimeHandling
import org.molap.questdb.readQuestDBJson

fun swissgridNetwork(dateTimeTz: DateTimeTz,
                     physicalNetwork: PhysicalNetwork,
                     geometrySubstationJson: String?,
                     eodSubstationJson: String?,
                     geometryLineJson: String?,
                     riskLineJson: String?,
                     weatherLineJson: String?,
): SubstationNetwork {
    val logicalNetwork = physicalNetwork.logical

    val network = buildSustationNetwork(dateTimeTz, physicalNetwork) {
        val geometrySubstationDf: DataFrame<Any?, String, Any?> by lazy {
            val dataFrame = DataFrame.readQuestDBJson(geometrySubstationJson!!, DateTimeHandling.Instant,
                "logicalGeometry", "physicalGeometry"
            ).reindexRowsUsingColumnDefault(true, "logicalBusId")
            dataFrame
        }
        val eodSubstationDf: DataFrame<Int, String, Any?> by lazy {
            val dataFrame = DataFrame.readQuestDBJson(eodSubstationJson!!, DateTimeHandling.Instant)
            dataFrame
        }
        val geometryLineDf: DataFrame<Any?, String, Any?> by lazy {
            val dataFrame = DataFrame.readQuestDBJson(geometryLineJson!!, DateTimeHandling.Instant,
                "logicalGeometry", "physicalGeometry"
            ).reindexRowsUsingColumnDefault(true, "logicalBranchId")
            dataFrame
        }
        val riskLineDf: DataFrame<Any?, String, Any?> by lazy {
            val dataFrame = DataFrame.readQuestDBJson(riskLineJson!!, DateTimeHandling.Instant)
                .reindexRowsUsingColumnDefault(true, "logicalBranchId")
            dataFrame
        }
        val weatherLineDf: DataFrame<Any?, String, Any?> by lazy {
            val dataFrame = DataFrame.readQuestDBJson(weatherLineJson!!, DateTimeHandling.Instant)
                .reindexRowsUsingColumnDefault(true, "logicalBranchId")
            dataFrame
        }

        val geometryCapability = GeometryNetworkCapability(
            substations = geometrySubstationDf.rowIndex.keys().map { it -> if(it != null) logicalBusIdOf(it.toString()) else null }.requireNoNulls().toSet(),
            lines = geometryLineDf.rowIndex.keys().map { it -> if(it != null) logicalBranchIdOf(it.toString()) else null }.requireNoNulls().toSet(),
            substationData = { logicalBusId ->
                val contained by lazy { geometrySubstationDf.rowIndex.contains(logicalBusId.name) }
                LazyGeometrySubstationData(
                    { if (contained) geometrySubstationDf.getValueAt(logicalBusId.name, "name") as String? else null },
                    { if (contained) geometrySubstationDf.getValueAt(logicalBusId.name, "type") as String? else null },
                    { if (contained) geometrySubstationDf.getValueAt(logicalBusId.name, "logicalGeometry") as Geometry? else null },
                    { if (contained) geometrySubstationDf.getValueAt(logicalBusId.name, "physicalGeometry") as Geometry? else null }
                )
            },
            lineData = { logicalBranchId ->
                val contained by lazy { geometryLineDf.rowIndex.contains(logicalBranchId.name) }
                LazyGeometryLineData(
                    { if (contained) geometryLineDf.getValueAt(logicalBranchId.name, "name") as String? else null },
                    { if (contained) geometryLineDf.getValueAt(logicalBranchId.name, "logicalGeometry") as Geometry? else null },
                    { if (contained) geometryLineDf.getValueAt(logicalBranchId.name, "physicalGeometry") as Geometry? else null }
                )
            }
        )
        add(geometryCapability)

        val powerCapability = PowerNetworkCapability(
            { logicalBusId ->
                object : PowerSubstationData {
                    override val baskv: Quantity<Voltage>?
                        get() = logicalNetwork.logicalNodes[logicalBusId]?.primary?.record?.baskv
                    override val vm: Quantity<PerUnit>?
                        get() = logicalNetwork.logicalNodes[logicalBusId]?.primary?.record?.vm
                }
            },
            { logicalBranchId ->
                object : PowerLineData {
                    override val dp: Quantity<Power>?
                        get() = logicalNetwork.logicalLinks[logicalBranchId]?.branch?.dp
                    override val ratea: Quantity<ApparentPower>?
                        get() = logicalNetwork.logicalLinks[logicalBranchId]?.ratea
                    override val pratea: Double?
                        get() = logicalNetwork.logicalLinks[logicalBranchId]?.pratea
                }
            },
        )
        add(powerCapability)

        val eodapability = EODNetworkCapability(
            substationData = { logicalBusId ->
                val name = geometryCapability.substationData(logicalBusId).name
                val voltage = powerCapability.substationData(logicalBusId).baskv
                val firstRow by lazy {
                    eodSubstationDf.rows().firstOrNull { row ->
                        eodSubstationDf.getValueAt(row, "UW Name") == name &&
                                (eodSubstationDf.getValueAt(row, "Spannungsebene") == null ||
                                        (eodSubstationDf.getValueAt(row, "Spannungsebene") as String).contains("${voltage?.amount?.toInt()}kV"))
                    }
                }
                val contained by lazy { firstRow != null }
                object : EODSubstationData {
                    override val uwName: String?
                        get() = if (contained) eodSubstationDf.getValueAt(firstRow!!, "UW Name") as String? else null
                    override val salesforce: String?
                        get() = if (contained) eodSubstationDf.getValueAt(firstRow!!, "Salesforce") as String? else null
                    override val tDMS: String?
                        get() = if (contained) eodSubstationDf.getValueAt(firstRow!!, "tDMS") as String? else null
                    override val dMZUWHSZ: String?
                        get() = if (contained) eodSubstationDf.getValueAt(firstRow!!, "DMZUWHSZ") as String? else null
                    override val sapAssetViewer: String?
                        get() = if (contained) eodSubstationDf.getValueAt(firstRow!!, "SAP Asset Viewer") as String? else null
                }
            }
        )
        add(eodapability)

        val riskCapability = RiskNetworkCapability(
            { logicalBranchId ->
                object : RiskLineData {
                    val contained = riskLineDf.rowIndex.contains(logicalBranchId.name)
                    override val probability: Double?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "probability") as Double? else null
                    override val riskMap: Int?
                        get() = if(contained) (riskLineDf.getValueAt(logicalBranchId.name, "risk_map") as Number?)?.toInt() else null
                    override val riskList: String?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "risk_list") as String? else null
                    override val assetCount: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "asset_count") as Int? else null
                    override val temperature_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "temperature_count") as Int? else null
                    override val wind_gusts_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "wind_gusts_count") as Int? else null
                    override val lightning_expected_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "lightning_expected_count") as Int? else null
                    override val lightning_hit_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "lightning_hit_count") as Int? else null
                    override val lightning_activity_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "lightning_activity_count") as Int? else null
                    override val flooding_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "flooding_count") as Int? else null
                    override val mudslide_count: Int?
                        get() = if(contained) riskLineDf.getValueAt(logicalBranchId.name, "mudslide_count") as Int? else null
                    override val details by lazy {
                        if(contained) {
                            val str = riskLineDf.getValueAt(logicalBranchId.name, "details") as String?
                            if(str != null) {
                                JSON.parse<JsonValue>(str).asJsonArray()
                            } else {
                                null
                            }
                        } else {
                            null
                        }
                    }
                }
            },
        )
        add(riskCapability)

        val weatherCapability = WeatherNetworkCapability(
            { logicalBranchId ->
                object : WeatherLineData {
                    override val mint70m: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "t_70m_min") as Double?
                    override val maxt70m: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "t_70m_max") as Double?
                    override val wind_speed_70m_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "wind_speed_70m_min") as Double?
                    override val wind_speed_70m_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "wind_speed_70m_max") as Double?
                    override val wind_gusts_70m_1h_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "wind_gusts_70m_1h_min") as Double?
                    override val wind_gusts_70m_1h_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "wind_gusts_70m_1h_max") as Double?
                    override val precip_5min_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "precip_5min_min") as Double?
                    override val precip_5min_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "precip_5min_max") as Double?
                    override val precip_type_intensity_5min_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "precip_type_intensity_5min_min") as Double?
                    override val precip_type_intensity_5min_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "precip_type_intensity_5min_max") as Double?
                    override val power_line_oscillation_24h_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "power_line_oscillation_24h_min") as Double?
                    override val power_line_oscillation_24h_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "power_line_oscillation_24h_max") as Double?
                    override val weather_code_1h_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "weather_code_1h_min") as Double?
                    override val weather_code_1h_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "weather_code_1h_max") as Double?
                    override val tstorm_warning_1h_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "tstorm_warning_1h_min") as Double?
                    override val tstorm_warning_1h_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "tstorm_warning_1h_max") as Double?
                    override val air_density_70m_min: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "air_density_70m_min") as Double?
                    override val air_density_70m_max: Double?
                        get() = weatherLineDf.getValueAt(logicalBranchId.name, "air_density_70m_max") as Double?
                }
            },
        )
        add(weatherCapability)

        add(RiskComponentsCapability())
    }

    return network
}